import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { PhoneInput } from "@/components/ui/phone-input";
import { CreateOperatorSchema, createOperatorSchema } from "@/api.schemas";
import { useDepots } from "@/api/depots";
import { useCreateOperator } from "@/api/operators";

export function CreateOperatorForm({ onSuccess }: { onSuccess?: () => void }) {
  const form = useForm<CreateOperatorSchema>({
    resolver: zodResolver(createOperatorSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      documents: {
        license: "NOT_PROVIDED",
        insurance: "NOT_PROVIDED",
      },
      workSchedule: [{ day: 1, start: "09:00", end: "17:00" }],
      depotId: "",
      authMethod: "email",
      address: {
        line1: "",
        line2: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
        location: { lat: 0, lng: 0 },
      },
    },
  });

  const { data: depots, isLoading } = useDepots();
  const createOperatorMutation = useCreateOperator({
    onSuccess: () => {
      onSuccess?.();
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    createOperatorMutation.mutate(values);
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8">
        {Object.keys(form.formState.errors).length > 0 && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">
              Please fix the following errors:
              <ul className="mt-2 list-disc pl-5">
                {Object.entries(form.formState.errors).map(([key, error]) => (
                  <li key={key}>
                    {error?.message?.toString() ?? `Invalid ${key}`}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {createOperatorMutation.error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">
              Network Error:{" "}
              {createOperatorMutation.error instanceof Error
                ? createOperatorMutation.error.message
                : "Failed to create operator. Please try again."}
            </div>
          </div>
        )}

        {createOperatorMutation.isPending && (
          <div className="rounded-md bg-blue-50 p-4">
            <div className="text-sm text-blue-700">Creating operator...</div>
          </div>
        )}

        <FormField
          control={form.control}
          name="authMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Authentication Method</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select authentication method" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="email">Email Authentication</SelectItem>
                  <SelectItem value="sms">SMS Authentication</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="john@example.com" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <PhoneInput
                  placeholder="Phone Number"
                  {...field}
                  value={field.value}
                  onChange={(value) => field.onChange(value || "")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dateOfBirth"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date of Birth</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="depotId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Depot</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a depot" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent
                  position="popper"
                  className="z-[60]"
                  side="bottom"
                  align="start"
                  sideOffset={8}
                >
                  {isLoading ? (
                    <SelectItem value="loading">Loading depots...</SelectItem>
                  ) : depots && depots.length > 0 ? (
                    depots.map((depot) => (
                      <SelectItem key={depot.id} value={depot.id}>
                        {depot.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-depots">
                      No depots available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <FormLabel>Address</FormLabel>
          <AutocompleteAddress
            onAddressChange={(address) => {
              if (address) {
                form.setValue("address", {
                  line1: address.addressComponents.line1,
                  line2: address.addressComponents.line2 || "",
                  city: address.addressComponents.city,
                  province: address.addressComponents.state,
                  country: address.addressComponents.country,
                  postalCode: address.addressComponents.postalCode || "",
                  location: address.location,
                });
              }
            }}
            onLine2Change={(value) => {
              const currentAddress = form.getValues("address");
              if (currentAddress) {
                form.setValue(
                  "address",
                  {
                    ...currentAddress,
                    line2: value,
                  },
                  {
                    shouldValidate: false,
                  }
                );
              }
            }}
            placeholder="Search address"
          />
          <FormMessage />
        </div>

        <Button type="submit" disabled={createOperatorMutation.isPending}>
          {createOperatorMutation.isPending ? "Creating..." : "Create Operator"}
        </Button>
      </form>
    </Form>
  );
}
